// 地図画面(記録モード)
// 地図表示 + GPS 軌跡記録 + 行動データ表示
//
// 設計: map-view.js とほぼ同じだが、tracking.js で軌跡を蓄積し、地図に描画
// 戻る時に「保存しますか?」を確認

import { createMap, createPositionMarker, setupAccuracyCircle, updateAccuracyCircle, switchLayer, setupTrackLayer, updateTrack, setupFootprintsLayer, updateFootprints, isFootprintsEnabled, setupOsmHikingLayer, updateOsmHiking, TILE_LAYERS, maplibregl } from '../map.js';
import { getAllTracks, savePoi } from '../cache.js';
import { fetchHikingPaths, isOsmHikingEnabled } from '../osm-hiking.js';
import { startCompass, stopCompass, subscribeCompass, headingToCardinal } from '../compass.js';
import { isVoiceGuideEnabled, speak, cancelSpeech } from '../voice-guide.js';
import { haversineMeters } from '../tracking.js';
import { showScreen, toast, escapeHtml, isValidLatLng, fmtElevation, fmtDistance, fmtDuration, backToHome, showActionSheet, showConfirm, showInputDialog, applyViewportHeight, adjustMapControls } from '../ui.js';
import * as tracker from '../tracking.js';
import { checkRouteDeviation, resetDeviationState } from '../route-deviation.js';

let activeMap = null;
let activeWatchId = null;
let activeMarker = null;
let followMode = true;
let currentLayer = 'std';
let statsTickHandle = null;
let unsubTracker = null;
let currentPlan = null;
let planMarkers = [];
let osmFetchTimer = null;
let osmAbortController = null;
let compassUnsub = null;
let lastCompassHeading = null;
let speakMilestone = { lastElev: -Infinity };

function debouncedFetchOsm() {
  if (osmFetchTimer) clearTimeout(osmFetchTimer);
  osmFetchTimer = setTimeout(async () => {
    if (!activeMap) return;
    if (activeMap.getZoom() < 13) {
      updateOsmHiking(activeMap, { type: 'FeatureCollection', features: [] });
      return;
    }
    const b = activeMap.getBounds();
    const bbox = { south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() };
    if (osmAbortController) osmAbortController.abort();
    osmAbortController = new AbortController();
    const data = await fetchHikingPaths(bbox, osmAbortController.signal);
    if (activeMap) updateOsmHiking(activeMap, data);
  }, 1500);
}

// opts: { plan?: Plan } — 計画つき記録モード(警告つき)
export function showMapRecord(opts = {}) {
  cleanupMapRecord();
  currentPlan = opts.plan || null;
  resetDeviationState();

  const html = `
    <div class="map-stage" id="map-stage">
      <div id="map-container" class="map-container" aria-label="登山地図(記録中)"></div>

      <div class="map-top">
        <button class="btn btn--icon" data-action="back" type="button" aria-label="戻る" style="background:var(--bg);border:1px solid var(--line);pointer-events:auto;">
          <svg class="icon"><use href="#i-back"/></svg>
        </button>
        <span class="rec-badge" id="rec-badge" style="pointer-events:auto;">
          <span class="rec-badge__dot"></span>
          <span id="rec-badge-text">REC</span>
        </span>
      </div>

      <div id="deviation-banner" class="warn-banner" hidden style="position:absolute;top:calc(56px + env(safe-area-inset-top));left:16px;right:16px;z-index:11;margin:0;">
        <svg class="icon"><use href="#i-warning"/></svg>
        <span id="deviation-text">ルートから離れています</span>
      </div>

      <div class="map-controls">
        <button class="btn btn--icon" data-action="zoom-in" type="button" aria-label="拡大">
          <svg class="icon"><use href="#i-zoom-in"/></svg>
        </button>
        <button class="btn btn--icon" data-action="zoom-out" type="button" aria-label="縮小">
          <svg class="icon"><use href="#i-zoom-out"/></svg>
        </button>
        <button class="btn btn--icon" data-action="follow" id="btn-follow" type="button" aria-label="現在地に追従">
          <svg class="icon"><use href="#i-target"/></svg>
        </button>
        <button class="btn btn--icon" data-action="layer" type="button" aria-label="地図レイヤー切替">
          <svg class="icon"><use href="#i-layers"/></svg>
        </button>
        <button class="btn btn--icon" data-action="photo" type="button" aria-label="写真撮影">
          <svg class="icon"><use href="#i-camera"/></svg>
        </button>
        <button class="btn btn--icon" data-action="poi" type="button" aria-label="地点メモ追加">
          <svg class="icon"><use href="#i-pin"/></svg>
        </button>
      </div>

      <div class="map-data">
        <div class="map-data__row">
          <div class="map-data__cell">
            <p class="map-data__label">distance</p>
            <p class="map-data__value" id="stat-distance">0.00 km</p>
          </div>
          <div class="map-data__cell">
            <p class="map-data__label">time</p>
            <p class="map-data__value" id="stat-time">0:00</p>
          </div>
          <div class="map-data__cell">
            <p class="map-data__label">elevation</p>
            <p class="map-data__value" id="stat-elev">+0 m</p>
          </div>
        </div>
        <div class="map-data__row">
          <div class="map-data__cell">
            <p class="map-data__label">pace</p>
            <p class="map-data__value" id="stat-pace" style="font-size:18px;">— km/h</p>
          </div>
          <div class="map-data__cell">
            <p class="map-data__label">compass</p>
            <p class="map-data__value" id="stat-compass" style="font-size:18px;">—</p>
          </div>
          <div class="map-data__cell">
            <p class="map-data__label">remain</p>
            <p class="map-data__value" id="stat-remain" style="font-size:18px;">—</p>
          </div>
        </div>
        <div class="map-data__actions">
          <button class="btn btn--ghost" id="btn-pause" type="button">
            <svg class="icon"><use href="#i-pause"/></svg>
            <span>一時停止</span>
          </button>
          <button class="btn btn--warn-ink" id="btn-stop" type="button">
            <svg class="icon"><use href="#i-stop"/></svg>
            <span>停止して保存</span>
          </button>
        </div>
      </div>
    </div>
  `;

  const screen = showScreen('map-record', html, { fullbleed: true });

  // PWA standalone の高さ問題対策: JS で実測値を直接設定
  const stage = screen.querySelector('#map-stage');
  applyViewportHeight(stage);
  setTimeout(() => adjustMapControls(stage), 100);
  window.addEventListener('resize', () => {
    applyViewportHeight(stage);
    adjustMapControls(stage);
  });
  window.addEventListener('orientationchange', () => setTimeout(() => {
    applyViewportHeight(stage);
    adjustMapControls(stage);
  }, 200));

  // 初期中心: localStorage の lastPosition、なければ富士山
  let initialCenter = [138.7274, 35.3606];
  let initialZoom = 14;
  try {
    const saved = JSON.parse(localStorage.getItem('sentier.lastPosition') || 'null');
    if (saved && isValidLatLng(saved.lat, saved.lng)) {
      initialCenter = [saved.lng, saved.lat];
    }
  } catch (e) {
    /* noop */
  }

  // screen の display:block 反映を待ってから MapLibre 初期化
  requestAnimationFrame(() => {
    activeMap = createMap('map-container', {
      center: initialCenter,
      zoom: initialZoom,
      layer: currentLayer,
    });

  setTimeout(() => { if (activeMap) activeMap.resize(); }, 200);

  activeMap.on('load', async () => {
    activeMap.resize();
    setupAccuracyCircle(activeMap);
    setupTrackLayer(activeMap, 'track', '#0a0a0a', 4);
    // 過去の足跡を背景に表示(設定 ON 時のみ)
    if (isFootprintsEnabled()) {
      setupFootprintsLayer(activeMap);
      const tracks = await getAllTracks();
      updateFootprints(activeMap, tracks);
    }
    // OSM 登山道オーバーレイ
    if (isOsmHikingEnabled()) {
      setupOsmHikingLayer(activeMap);
      debouncedFetchOsm();
    }
    // 計画ルートがあれば淡く描画
    if (currentPlan && currentPlan.waypoints?.length >= 2) {
      setupTrackLayer(activeMap, 'plan-route', '#5a5a58', 2);
      const pts = currentPlan.waypoints.map((w) => ({ lat: w.lat, lng: w.lng }));
      updateTrack(activeMap, 'plan-route', pts);
      // 計画 waypoint マーカー
      currentPlan.waypoints.forEach((w, i) => {
        const el = document.createElement('div');
        el.style.cssText = `width:14px;height:14px;border-radius:50%;background:var(--paper);border:1.5px solid var(--dim);`;
        const m = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([w.lng, w.lat])
          .addTo(activeMap);
        planMarkers.push(m);
      });
      // 全体表示
      const bounds = new maplibregl.LngLatBounds();
      currentPlan.waypoints.forEach((w) => bounds.extend([w.lng, w.lat]));
      activeMap.fitBounds(bounds, { padding: 80, duration: 400 });
    }
    startWatching();
  });

    activeMap.on('dragstart', () => setFollow(false));
    activeMap.on('moveend', () => {
      if (isOsmHikingEnabled()) debouncedFetchOsm();
    });
    // ロングタップ(contextmenu)で ActionSheet
    activeMap.on('contextmenu', (e) => handleLongPress(e.lngLat));
  });

  // コンパス開始
  startCompass().then((ok) => {
    if (!ok) return;
    compassUnsub = subscribeCompass((h) => {
      lastCompassHeading = h;
      const el = document.getElementById('stat-compass');
      if (el) el.textContent = `${headingToCardinal(h)} ${Math.round(h)}°`;
    });
  });

  // 音声ガイド: 起動アナウンス
  if (isVoiceGuideEnabled()) {
    speak('記録を開始します', { force: true });
  }

  bindControls(screen);

  // 戻るボタンは記録中の意図せぬ離脱を防ぐため、ここで個別バインド(伝播停止)
  screen.querySelector('[data-action="back"]')?.addEventListener('click', async (e) => {
    e.stopPropagation();
    await handleExit();
  }, true);

  // 記録開始
  tracker.start();
  tracker.acquireWakeLock();
  unsubTracker = tracker.subscribe(renderStats);

  // iOS の制約を初回のみ通知(localStorage で永続化)
  if (!localStorage.getItem('sentier.recordingHintSeen')) {
    setTimeout(() => {
      toast('画面オン推奨: ロック中は GPS 記録が一時停止します', 5000);
    }, 800);
    localStorage.setItem('sentier.recordingHintSeen', '1');
  }

  // 経過時間は毎秒更新
  statsTickHandle = setInterval(() => {
    renderStats(tracker.getStats(), tracker.getPoints());
  }, 1000);
}

function bindControls(screen) {
  screen.querySelector('[data-action="zoom-in"]')?.addEventListener('click', () => {
    if (activeMap) activeMap.zoomTo(activeMap.getZoom() + 1, { duration: 200 });
  });
  screen.querySelector('[data-action="zoom-out"]')?.addEventListener('click', () => {
    if (activeMap) activeMap.zoomTo(activeMap.getZoom() - 1, { duration: 200 });
  });
  screen.querySelector('[data-action="follow"]')?.addEventListener('click', () => {
    setFollow(true);
    centerOnLastPosition();
  });
  screen.querySelector('[data-action="layer"]')?.addEventListener('click', cycleLayer);

  screen.querySelector('[data-action="photo"]')?.addEventListener('click', async () => {
    const { captureAndSave } = await import('./photo.js');
    const trackId = tracker.getStats()?.id;
    await captureAndSave(null, () => {
      const pts = tracker.getPoints();
      if (pts.length === 0) return null;
      const last = pts[pts.length - 1];
      return { lat: last.lat, lng: last.lng, ele: last.ele };
    });
  });

  screen.querySelector('[data-action="poi"]')?.addEventListener('click', async () => {
    const pts = tracker.getPoints();
    if (pts.length === 0) {
      toast('まだ位置情報がありません');
      return;
    }
    const last = pts[pts.length - 1];
    const title = await showInputDialog({
      title: '地点メモを追加',
      body: '現在地に地点をマークします',
      placeholder: '例: 水場 / 分岐 / 滑った',
    });
    if (!title || !title.trim()) return;
    const { savePoi } = await import('../cache.js');
    await savePoi({
      lat: last.lat,
      lng: last.lng,
      ele: last.ele || null,
      title: title.trim(),
      kind: 'note',
    });
    toast('地点を保存しました');
  });

  screen.querySelector('#btn-pause')?.addEventListener('click', () => {
    const status = tracker.getStatus();
    if (status === 'recording') {
      tracker.pause();
      const btn = screen.querySelector('#btn-pause');
      btn.innerHTML = '<svg class="icon"><use href="#i-play"/></svg><span>再開</span>';
      screen.querySelector('#rec-badge-text').textContent = 'PAUSE';
      toast('一時停止しました');
    } else if (status === 'paused') {
      tracker.resume();
      const btn = screen.querySelector('#btn-pause');
      btn.innerHTML = '<svg class="icon"><use href="#i-pause"/></svg><span>一時停止</span>';
      screen.querySelector('#rec-badge-text').textContent = 'REC';
      toast('再開しました');
    }
  });

  screen.querySelector('#btn-stop')?.addEventListener('click', () => handleExit({ forceStop: true }));
}

// 共通: 終了処理(保存 or 破棄を判断してホームへ戻る)
async function handleExit(opts = {}) {
  const pts = tracker.getPoints();

  if (pts.length === 0) {
    const ok = await showConfirm({
      title: '記録を終了しますか?',
      body: 'まだ位置情報を記録できていません。',
      confirmLabel: '終了する',
      cancelLabel: '続行する',
    });
    if (!ok) return;
    tracker.discard();
    await tracker.releaseWakeLock();
    cleanupMapRecord();
    backToHome();
    return;
  }

  let choice;
  if (opts.forceStop) {
    const ok = await showConfirm({
      title: '記録を停止して保存しますか?',
      body: `${pts.length} 地点を記録しました`,
      confirmLabel: '保存して終了',
    });
    if (!ok) return;
    choice = 'save';
  } else {
    choice = await showActionSheet({
      title: 'この記録をどうしますか?',
      body: `${pts.length} 地点を記録しました`,
      options: [
        { label: '続行(記録を続ける)', value: 'cancel', kind: 'ghost' },
        { label: '保存して終了', value: 'save', kind: 'ink' },
        { label: '破棄して終了', value: 'discard', kind: 'warn' },
      ],
    });
    if (!choice || choice === 'cancel') return;
    if (choice === 'discard') {
      tracker.discard();
      await tracker.releaseWakeLock();
      cleanupMapRecord();
      backToHome();
      toast('記録を破棄しました');
      return;
    }
  }

  const saved = await tracker.stop();
  await tracker.releaseWakeLock();
  cleanupMapRecord();
  if (saved) toast(`「${saved.name}」を保存しました`);
  backToHome();
}

function renderStats(stats, points) {
  document.getElementById('stat-distance')?.replaceChildren(
    document.createTextNode(fmtDistance(stats.distance))
  );
  document.getElementById('stat-time')?.replaceChildren(
    document.createTextNode(fmtDuration(stats.duration))
  );
  document.getElementById('stat-elev')?.replaceChildren(
    document.createTextNode(`+${Math.round(stats.elevGain)} m`)
  );

  // 歩行ペース
  const paceEl = document.getElementById('stat-pace');
  if (paceEl) {
    const kmh = (stats.currentPace || 0) * 3.6;
    paceEl.textContent = kmh > 0.1 ? `${kmh.toFixed(1)} km/h` : '— km/h';
  }

  // あと何 m (計画山頂までの距離・標高差)
  const remainEl = document.getElementById('stat-remain');
  if (remainEl) {
    const last = points[points.length - 1];
    const summit = findPlanSummit();
    if (summit && last) {
      const d = haversineMeters(last.lat, last.lng, summit.lat, summit.lng);
      const eleDiff = (typeof summit.ele === 'number' && typeof last.ele === 'number') ? Math.round(summit.ele - last.ele) : null;
      remainEl.textContent = eleDiff !== null
        ? `${d > 1000 ? (d/1000).toFixed(1)+'km' : Math.round(d)+'m'} / +${eleDiff}m`
        : `${d > 1000 ? (d/1000).toFixed(1)+'km' : Math.round(d)+'m'}`;
    } else {
      remainEl.textContent = '—';
    }
  }

  // 軌跡を地図に描画
  if (activeMap && points.length >= 2) {
    updateTrack(activeMap, 'track', points);
  }

  // 音声ガイド: 標高 500m 単位のマイルストーン
  if (isVoiceGuideEnabled() && stats.elevGain > 0) {
    const milestone = Math.floor(stats.elevGain / 500) * 500;
    if (milestone > 0 && milestone > speakMilestone.lastElev) {
      speak(`累積標高 ${milestone} メートルを超えました`);
      speakMilestone.lastElev = milestone;
    }
  }
}

// 計画があれば山頂(最後の waypoint または最高標高 waypoint) を返す
function findPlanSummit() {
  if (!currentPlan || !Array.isArray(currentPlan.waypoints) || currentPlan.waypoints.length === 0) return null;
  // 最高標高の waypoint
  let summit = null;
  for (const w of currentPlan.waypoints) {
    if (typeof w.ele !== 'number') continue;
    if (!summit || w.ele > summit.ele) summit = w;
  }
  // 標高情報なければ中央の waypoint(慣例的に山頂)を使う
  if (!summit && currentPlan.waypoints.length >= 3) {
    summit = currentPlan.waypoints[Math.floor(currentPlan.waypoints.length / 2)];
  }
  return summit;
}

async function handleLongPress(lngLat) {
  const { showActionSheet, showInputDialog } = await import('../ui.js');
  const choice = await showActionSheet({
    title: '地図上のこの地点',
    body: `${lngLat.lat.toFixed(5)}, ${lngLat.lng.toFixed(5)}`,
    options: [
      { label: '地点メモを追加', value: 'poi', kind: 'ink' },
      { label: '座標をコピー', value: 'copy', kind: 'ghost' },
    ],
  });
  if (!choice) return;
  if (choice === 'copy') {
    const { copyToClipboard } = await import('../ui.js');
    await copyToClipboard(`${lngLat.lat.toFixed(5)}, ${lngLat.lng.toFixed(5)}`, '座標をコピーしました');
  } else if (choice === 'poi') {
    const title = await showInputDialog({
      title: '地点メモ',
      placeholder: '例: 水場 / 分岐 / 滑った',
    });
    if (!title) return;
    await savePoi({
      lat: lngLat.lat,
      lng: lngLat.lng,
      title: title.trim(),
      kind: 'note',
    });
    toast('地点を保存しました');
  }
}

function cycleLayer() {
  const order = ['std', 'pale', 'relief', 'photo', 'vector'];
  const idx = order.indexOf(currentLayer);
  currentLayer = order[(idx + 1) % order.length];
  switchLayer(activeMap, currentLayer);
  activeMap.once('idle', async () => {
    setupAccuracyCircle(activeMap);
    setupTrackLayer(activeMap, 'track', '#0a0a0a', 4);
    updateTrack(activeMap, 'track', tracker.getPoints());
    if (isFootprintsEnabled()) {
      setupFootprintsLayer(activeMap);
      const tracks = await getAllTracks();
      updateFootprints(activeMap, tracks);
    }
    if (isOsmHikingEnabled()) {
      setupOsmHikingLayer(activeMap);
      debouncedFetchOsm();
    }
    centerOnLastPosition();
  });
  toast(`地図: ${TILE_LAYERS[currentLayer].name}`);
}

function setFollow(on) {
  followMode = on;
  const btn = document.getElementById('btn-follow');
  if (btn) {
    btn.style.background = on ? 'var(--ink)' : 'var(--bg)';
    btn.style.color = on ? 'var(--bg)' : 'var(--ink)';
  }
}

function centerOnLastPosition() {
  try {
    const saved = JSON.parse(localStorage.getItem('sentier.lastPosition') || 'null');
    if (saved && isValidLatLng(saved.lat, saved.lng) && activeMap) {
      activeMap.easeTo({ center: [saved.lng, saved.lat], duration: 400 });
    }
  } catch (e) {
    /* noop */
  }
}

function startWatching() {
  if (!('geolocation' in navigator)) {
    toast('この端末では位置情報を取得できません');
    return;
  }

  activeWatchId = navigator.geolocation.watchPosition(
    (pos) => onPositionUpdate(pos),
    (err) => onPositionError(err),
    {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 15000,
    }
  );
}

function onPositionUpdate(pos) {
  if (!activeMap) return;
  const { latitude, longitude, accuracy, altitude } = pos.coords;
  if (!isValidLatLng(latitude, longitude)) return;

  try {
    localStorage.setItem(
      'sentier.lastPosition',
      JSON.stringify({ lat: latitude, lng: longitude, ts: Date.now() })
    );
  } catch (e) { /* noop */ }

  // tracker へ追加
  tracker.addPoint({
    lat: latitude,
    lng: longitude,
    ele: typeof altitude === 'number' ? altitude : null,
    ts: Date.now(),
    accuracy: typeof accuracy === 'number' ? accuracy : null,
  });

  if (activeMarker) {
    activeMarker.setLngLat([longitude, latitude]);
  } else {
    activeMarker = createPositionMarker(activeMap, longitude, latitude);
  }

  if (typeof accuracy === 'number' && accuracy > 0) {
    updateAccuracyCircle(activeMap, longitude, latitude, accuracy);
  }

  if (followMode) {
    activeMap.easeTo({ center: [longitude, latitude], duration: 350 });
  }

  // 計画ルートとの逸脱チェック
  if (currentPlan && currentPlan.waypoints?.length >= 2) {
    const { level, distance } = checkRouteDeviation(latitude, longitude, currentPlan.waypoints);
    updateDeviationBanner(level, distance);
  }
}

function updateDeviationBanner(level, distance) {
  const banner = document.getElementById('deviation-banner');
  const text = document.getElementById('deviation-text');
  if (!banner || !text) return;
  if (level === 'ok' || distance === null) {
    banner.hidden = true;
    return;
  }
  banner.hidden = false;
  banner.style.background = level === 'danger' ? 'var(--warn)' : 'var(--warn-bg)';
  banner.style.color = level === 'danger' ? '#ffffff' : 'var(--warn)';
  banner.style.borderColor = 'var(--warn)';
  text.textContent = `ルートから ${Math.round(distance)} m 離れています`;
}

function onPositionError(err) {
  let msg = '位置情報を取得できません';
  if (err.code === 1) msg = '位置情報の利用が拒否されています';
  else if (err.code === 2) msg = '位置情報が取得できません(電波弱)';
  else if (err.code === 3) msg = '位置情報の取得がタイムアウトしました';
  toast(msg);
  console.warn('Geolocation error', err);
}

export function cleanupMapRecord() {
  if (statsTickHandle) {
    clearInterval(statsTickHandle);
    statsTickHandle = null;
  }
  if (unsubTracker) {
    unsubTracker();
    unsubTracker = null;
  }
  if (activeWatchId !== null) {
    navigator.geolocation.clearWatch(activeWatchId);
    activeWatchId = null;
  }
  if (activeMarker) {
    activeMarker.remove();
    activeMarker = null;
  }
  planMarkers.forEach((m) => m.remove());
  planMarkers = [];
  if (compassUnsub) { compassUnsub(); compassUnsub = null; }
  stopCompass();
  cancelSpeech();
  if (activeMap) {
    activeMap.remove();
    activeMap = null;
  }
  currentPlan = null;
  speakMilestone = { lastElev: -Infinity };
}
